<template>
  <div class="d-flex flex-column">
    <div class="header" v-if="route != 'Landing' && user">
      <div class="d-flex flex-row align-center pa-4">
        <v-btn icon color="black"><v-icon>mdi-lightning-bolt</v-icon></v-btn>
        <div class="overline ml-2" style="font-size: 18px !important; font-weight: 300;">
          Hydro Ottawa AI
        </div>
        <v-spacer></v-spacer>
        <div v-if="user" class="d-flex flex-row align-center">
          <!-- Account Menu -->
          <v-menu offset-y bottom left :key="user.email">
            <template v-slot:activator="{ on, attrs }">
              <v-btn text v-bind="attrs" v-on="on" class="text-capitalize">
                {{ user.displayName }}
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <!-- User Info Section -->
              <v-list-item>
                <v-list-item-avatar>
                  <v-img v-if="user.photoURL" :src="user.photoURL"></v-img>
                  <v-icon v-else>mdi-account-circle</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ user.displayName }}</v-list-item-title>
                  <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              
              <v-divider></v-divider>
              
              <!-- Admin Section (if applicable) -->
              <template v-if="user && (user.role === 'Manager' || user.role === 'Admin')">
                
                <!-- Management Options -->
                <v-list-item v-if="(user.role === 'Manager' || user.role === 'Admin')" link @click="go('UserManagement')">
                  <v-list-item-icon>
                    <v-icon>mdi-account-supervisor</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ user.role === 'Manager' ? 'Manage My Teams' : 'User Management' }}
                  </v-list-item-title>
                </v-list-item>
                
                <v-list-item v-if="user.role === 'Admin'" link @click="go('UseCaseRequestsAdmin')">
                  <v-list-item-icon>
                    <v-icon>mdi-lightbulb-on-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Use Case Requests</v-list-item-title>
                </v-list-item>
                
                <v-list-item v-if="user.role === 'Admin'" link @click="go('UserMigration')">
                  <v-list-item-icon>
                    <v-icon>mdi-database-sync</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>User Migration</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="user.role === 'Admin'" link @click="go('CallAnalysisPipeline')">
                  <v-list-item-icon>
                    <v-icon>mdi-lightning-bolt</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Call Analysis Pipeline</v-list-item-title>
                </v-list-item>
                
                <v-divider></v-divider>
              </template>
              
              <!-- Account -->
              <v-list-item link @click="go('Account')">
                <v-list-item-icon>
                  <v-icon>mdi-account-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Account</v-list-item-title>
              </v-list-item>
              
              <v-divider></v-divider>
              
              <!-- Logout -->
              <v-list-item link @click="logout()">
                <v-list-item-icon>
                  <v-icon>mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Logout</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <!-- View Mode Toggle -->
        <!-- <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              text 
              small
              v-bind="attrs" 
              v-on="on" 
              @click="toggleViewMode"
              class="ml-2 text-capitalize"
            >
              <v-icon left small>{{ viewMode === 'groups' ? 'mdi-view-grid' : 'mdi-view-module' }}</v-icon>
              {{ viewMode === 'groups' ? 'Groups' : 'Divisions' }}
            </v-btn>
          </template>
          <span>Switch to {{ viewMode === 'groups' ? 'Division' : 'Group' }} View</span>
        </v-tooltip> -->
        
        <v-btn color="primary" @click="go('CreateRequest')" rounded class="ml-2" left>
          <v-icon small left>mdi-creation</v-icon>
          <span class="text-capitalize">Create Request</span>
        </v-btn>
      </div>
    </div>
    
    <div v-if="user && hasPermissions('Employee')" class="subheader">
      <v-tabs background-color="primary" dark>
        <v-tab @click="go('Dashboard')">
          Dashboard
        </v-tab>
        
        <!-- Division-based View -->
        <template v-if="viewMode === 'divisions'">
          <v-menu 
            v-for="division in userMenu" 
            :key="division.id" 
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-tab v-bind="attrs" v-on="on">
                <v-icon v-if="division.icon" left small>{{ division.icon }}</v-icon>
                {{ division.name }}
              </v-tab>
            </template>
            
            <v-list v-if="division.groups && division.groups.length">
              <div v-for="(group, groupIndex) in division.groups" :key="group.id">
                <!-- Group Header -->
                <v-subheader v-if="group.tools && group.tools.length > 0" class="font-weight-bold">
                  <v-icon v-if="group.icon" left small>{{ group.icon }}</v-icon>
                  {{ group.name }}
                </v-subheader>
                
                <!-- Tools in this Group -->
                <v-list-item 
                  v-for="tool in group.tools" 
                  :key="tool.id"
                  link 
                  @click="go(getToolRouteName(tool))"
                >
                  <v-list-item-icon class="mr-4">
                    <v-icon v-if="tool.status === 'experimental'" color="orange">mdi-flask</v-icon>
                    <v-icon v-else-if="tool.icon" :color="getToolIconColor(tool)">{{ tool.icon }}</v-icon>
                    <v-icon v-else>mdi-application</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="text-left">
                    {{ tool.name }}
                    <v-chip 
                      v-if="tool.status === 'experimental'" 
                      x-small 
                      color="orange" 
                      text-color="white"
                      class="ml-2"
                    >
                      PILOT
                    </v-chip>
                  </v-list-item-title>
                </v-list-item>
                
                <!-- Divider between groups (except last) -->
                <v-divider 
                  v-if="groupIndex < division.groups.length - 1 && group.tools && group.tools.length > 0" 
                  class="my-2"
                ></v-divider>
              </div>
            </v-list>
          </v-menu>
        </template>
        
        <!-- Group-based View -->
        <template v-else>
          <v-menu 
            v-for="group in userMenuFlattened" 
            :key="group.id" 
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-tab v-bind="attrs" v-on="on">
                <v-icon v-if="group.icon" left small>{{ group.icon }}</v-icon>
                {{ group.name }}
              </v-tab>
            </template>
            
            <v-list v-if="group.tools && group.tools.length">
              <!-- Division Context (small subtitle) -->
              <v-subheader class="caption text--secondary">
                <v-icon v-if="group.divisionIcon" left x-small>{{ group.divisionIcon }}</v-icon>
                {{ group.divisionName }}
              </v-subheader>
              
              <!-- Tools in this Group -->
              <v-list-item 
                v-for="tool in group.tools" 
                :key="tool.id"
                link 
                @click="go(getToolRouteName(tool))"
              >
                <v-list-item-icon class="mr-4">
                  <v-icon v-if="tool.status === 'experimental'" color="orange">mdi-flask</v-icon>
                  <v-icon v-else-if="tool.icon" :color="getToolIconColor(tool)">{{ tool.icon }}</v-icon>
                  <v-icon v-else>mdi-application</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="text-left">
                  {{ tool.name }}
                  <v-chip 
                    v-if="tool.status === 'experimental'" 
                    x-small 
                    color="orange" 
                    text-color="white"
                    class="ml-2"
                  >
                    PILOT
                  </v-chip>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        
        <!-- <v-tab @click="go('News')">
          News
        </v-tab> -->
      </v-tabs>
    </div>
  </div>
</template>

<script>
import { 
  generateUserMenu, 
  userHasToolAccess,
  userCanManageGroup
} from '@/config/toolsConfig'

export default {
  name: 'Header',
  props: ['user'],
  data: () => ({
    route: null,
    viewMode: localStorage.getItem('headerViewMode') || 'groups' // 'divisions' or 'groups'
  }),
  
  computed: {
    userMenu() {
      if (!this.user) return []
      
      try {
        return generateUserMenu(this.user)
      } catch (error) {
        console.error('Error generating user menu:', error)
        return []
      }
    },
    
    userMenuFlattened() {
      if (!this.user) return []
      
      try {
        const divisionMenu = generateUserMenu(this.user)
        const flattenedGroups = []
        
        // Flatten all groups from all divisions
        divisionMenu.forEach(division => {
          if (division.groups && division.groups.length > 0) {
            division.groups.forEach(group => {
              if (group.tools && group.tools.length > 0) {
                flattenedGroups.push({
                  ...group,
                  divisionName: division.name,
                  divisionIcon: division.icon
                })
              }
            })
          }
        })
        
        return flattenedGroups
      } catch (error) {
        console.error('Error generating flattened user menu:', error)
        return []
      }
    },
    
    activeMenu() {
      return this.viewMode === 'groups' ? this.userMenuFlattened : this.userMenu
    }
  },
  
  created() {
    this.route = this.$route.name
  },
  
  methods: {
    logout() {
      this.$emit('logout')
    },
    
    hasPermissions(requirements) {
      if (!this.user) return false
      if (this.user.role === 'Admin') return true
      if (!requirements) return true
      
      if (typeof requirements === 'string') requirements = [requirements]
      
      // Check if user's role matches any of the requirements
      // Using role hierarchy: '', Employee, Pioneer, Manager, Admin
      const roleHierarchy = ['', 'Employee', 'Pioneer', 'Manager', 'Admin']
      const userLevel = roleHierarchy.indexOf(this.user.role || '')
      
      return requirements.some(requirement => {
        const requiredLevel = roleHierarchy.indexOf(requirement)
        return userLevel >= requiredLevel
      })
    },

    hasToolAccess(toolId) {
      if (!this.user) return false
      try {
        return userHasToolAccess(this.user, toolId)
      } catch (error) {
        console.error('Error checking tool access:', error)
        return false
      }
    },

    getToolRouteName(tool) {
      // Convert tool route to router name
      // e.g., '/customer/service/call-analysis' -> 'CustomerServiceCallAnalysis'
      if (tool.route) {
        // For now, use the tool.id as the route name
        // This should match the route names in your router
        return tool.id
      }
      return tool.id
    },

    getToolIconColor(tool) {
      switch (tool.status) {
        case 'experimental':
          return 'orange'
        case 'beta':
          return 'blue'
        case 'deprecated':
          return 'grey'
        default:
          return 'primary'
      }
    },

    routeExists(routeName) {
      try {
        const route = this.$router.resolve({ name: routeName })
        return route && route.resolved && route.resolved.matched.length > 0
      } catch (error) {
        return false
      }
    },
    
    toggleViewMode() {
      this.viewMode = this.viewMode === 'groups' ? 'divisions' : 'groups'
      localStorage.setItem('headerViewMode', this.viewMode)
    }
  },
  
  watch: {
    $route(to, from) {
      this.route = to.name
    },
  }
}
</script>

<style lang="scss">
.header {
  background: white;
  z-index: 1;
  -webkit-box-shadow: 0px 0px 9px 3px rgba(41,41,41,.25);
  -moz-box-shadow: 0px 0px 9px 3px rgba(41,41,41,.25);
  box-shadow: 0px 0px 9px 3px rgba(41,41,41,.25);
}

.subheader {
  background: black;
  -webkit-box-shadow: 0px 0px 9px 3px rgba(41,41,41,.25);
  -moz-box-shadow: 0px 0px 9px 3px rgba(41,41,41,.25);
  box-shadow: 0px 0px 9px 3px rgba(41,41,41,.25);
}

// Custom styling for menu items
.v-list-item {
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.v-subheader {
  color: rgba(0, 0, 0, 0.6) !important;
  font-weight: 600 !important;
}
</style>